import React from 'react'
import Markdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import remarkMath from 'remark-math'

interface SafeMarkdownProps {
  children: string
  className?: string
  components?: any
}

/**
 * Safe Markdown wrapper component that handles errors gracefully
 * Provides consistent markdown rendering with enhanced table support and fallback formatting
 */
export const SafeMarkdown: React.FC<SafeMarkdownProps> = ({
  children,
  className = '',
  components = {},
}) => {
  try {
    // Clean the content to prevent table parsing issues
    const cleanContent = children || ''

    // Pre-process content to handle problematic table syntax
    const processedContent = cleanContent
      .replace(/\|/g, '&#124;') // Escape pipe characters that cause table parsing issues
      .replace(/&#124;\s*&#124;/g, '|') // Keep proper table structure

    return (
      <div className={className}>
        <Markdown
          rehypePlugins={[rehypeRaw] as any}
          remarkPlugins={[
            // Use remarkMath without remarkGfm to avoid table parsing issues
            remarkMath,
          ]}
          components={{
            // Default components for basic formatting
            p: ({ children }) => <p className="mb-2">{children}</p>,
            ul: ({ children }) => <ul className="list-disc ml-4 mb-2">{children}</ul>,
            ol: ({ children }) => <ol className="list-decimal ml-4 mb-2">{children}</ol>,
            li: ({ children }) => <li className="mb-1">{children}</li>,
            strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
            em: ({ children }) => <em className="italic">{children}</em>,
            code: ({ children }) => <span>{children}</span>,
            blockquote: ({ children }) => (
              <blockquote className="border-l-4 border-gray-300 pl-4 italic">{children}</blockquote>
            ),
            h1: ({ children }) => <h1 className="text-2xl font-bold mb-4">{children}</h1>,
            h2: ({ children }) => <h2 className="text-xl font-bold mb-3">{children}</h2>,
            h3: ({ children }) => <h3 className="text-lg font-bold mb-2">{children}</h3>,
            // Handle tables manually with simple rendering
            table: ({ children }) => (
              <div className="overflow-x-auto mb-4">
                <table className="min-w-full border-collapse border border-gray-300">
                  {children}
                </table>
              </div>
            ),
            thead: ({ children }) => <thead className="bg-gray-100">{children}</thead>,
            tbody: ({ children }) => <tbody>{children}</tbody>,
            tr: ({ children }) => <tr className="border-b border-gray-200">{children}</tr>,
            td: ({ children }) => <td className="px-3 py-2 border border-gray-300">{children}</td>,
            th: ({ children }) => (
              <th className="px-3 py-2 border border-gray-300 font-semibold text-left">
                {children}
              </th>
            ),
            // Allow custom components to override defaults
            ...components,
          }}
        >
          {processedContent}
        </Markdown>
      </div>
    )
  } catch (error) {
    console.warn('Markdown parsing error:', error)
    // Enhanced fallback with basic HTML-like formatting
    const fallbackContent = children
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/`(.*?)`/g, '<code>$1</code>')
      .replace(/\n/g, '<br/>')

    return (
      <div
        className={className}
        style={{ whiteSpace: 'pre-wrap' }}
        dangerouslySetInnerHTML={{ __html: fallbackContent }}
      />
    )
  }
}

export default SafeMarkdown
